// onboardingFlag.js — local persistent flag so a successful Onboarding
// finish() survives an unreliable Firestore write.
//
// Why this exists (2026-05-26 fix):
//   The Onboarding `finish()` writes `profile.onboardingComplete: true`
//   to Firestore inside a 15s Promise.race timeout. Under flaky cellular
//   connections (especially in WKWebView), the SDK can resolve the write
//   slightly past the timeout — the user sees an error, force-quits,
//   and on the NEXT launch their profile read returns `{}` (the timeout
//   we ALSO added in App.jsx fired first) or comes back without
//   `onboardingComplete: true` (the write didn't actually land).
//   Either way the user is bounced back to /onboarding even though they
//   completed the flow.
//
//   The local flag closes that gap: as soon as the user reaches the
//   finish() entry, we set this flag BEFORE the Firestore await. Even
//   if the network write fails, App.jsx's RequireOnboarded gate honors
//   the local flag as a fallback signal and lets the user past. On a
//   subsequent successful launch with a clean profile read, the
//   Firestore value will catch up and the flag becomes redundant — but
//   leaving it set doesn't hurt (the gate uses OR semantics).
//
//   Per-uid keying prevents one user's "completed" flag from leaking
//   onto a different account on the same device (e.g. shared family
//   iPad).

const KEY_PREFIX = 'vinarai_onboarding_done_'

/**
 * Returns true if the local fallback flag is set for the given uid.
 * Safe to call when uid is null/undefined (returns false).
 */
export function getLocalOnboardingDone(uid) {
  if (!uid) return false
  try {
    return localStorage.getItem(KEY_PREFIX + uid) === '1'
  } catch {
    // Private browsing / Safari sandbox quirks. Falling through
    // to "not done" makes the gate strictly less permissive, which
    // is the safe direction (worst case: user re-sees onboarding,
    // which is annoying but not data-loss).
    return false
  }
}

/**
 * Set / clear the local fallback flag for a uid. Called from
 * Onboarding.finish() AT THE TOP so the flag lands even if the
 * Firestore write fails downstream. Idempotent.
 */
export function setLocalOnboardingDone(uid, done = true) {
  if (!uid) return
  try {
    if (done) localStorage.setItem(KEY_PREFIX + uid, '1')
    else      localStorage.removeItem(KEY_PREFIX + uid)
  } catch { /* private mode — flag just doesn't persist */ }
}
